import { Link, createFileRoute } from "@tanstack/react-router";

import { AutoScale } from "../components/site/AutoScale";
import { Footer } from "../components/site/Footer";
import { Header } from "../components/site/Header";
import { ScrollToTop } from "../components/site/ScrollToTop";
import { SpecialOffer } from "../components/home/SpecialOffer";
import { SITE_URL } from "../lib/site";

const title = "Редакционная политика блога HAY.agency";
const description =
  "Как редакция HAY.agency выбирает темы, проверяет факты, работает с источниками, отмечает публикацию и обновление материалов и исправляет ошибки.";
const url = `${SITE_URL}/blog/editorial-policy`;
const image = `${SITE_URL}/og/blog.webp`;

const SECTIONS: { heading: string; body: string }[] = [
  {
    heading: "Кто создаёт материалы",
    body: "Статьи готовит редакция агентства — специалисты по разработке, SEO, рекламе и CRM вместе с редактором. Если материал подготовил конкретный сотрудник, он указывается автором; в остальных случаях автором выступает редакция.",
  },
  {
    heading: "Как выбираются темы",
    body: "Мы пишем о задачах, с которыми к нам приходят клиенты: запуск и рост каналов продаж, продвижение, реклама, аналитика и автоматизация. Тема попадает в план, если у команды есть практический опыт или доступ к первоисточникам.",
  },
  {
    heading: "Как проверяются факты",
    body: "Цифры, тарифы, требования площадок и правовые нормы сверяются с первоисточником. Если данные могут измениться, мы фиксируем дату обращения к источнику. Материалы, которые ещё ждут проверки после переноса, помечаются во внутреннем редакционном статусе и обновляются по мере верификации.",
  },
  {
    heading: "Как используются внешние источники",
    body: "Мы ссылаемся на официальную документацию, справочные центры площадок, исследования и нормативные акты. Ссылки на такие источники открытые и не закрываются атрибутом nofollow. Nofollow, sponsored и ugc используются только по назначению.",
  },
  {
    heading: "Как отмечаются публикация и обновление",
    body: "Дата публикации фиксируется один раз и не меняется. Дата обновления появляется только после реальной проверки и содержательной правки текста — техническая миграция, редизайн или пересборка сайта датой обновления не считаются.",
  },
  {
    heading: "Как исправляются ошибки",
    body: "Если в тексте найдена ошибка, мы исправляем её и, если правка меняет смысл, обновляем дату изменения. Сообщить о неточности можно через страницу контактов — мы отвечаем на такие письма.",
  },
  {
    heading: "Как отделяются мнение и факт",
    body: "Факты сопровождаются источником, оценки и прогнозы формулируются как позиция редакции. Мы не выдаём собственные предположения за данные площадок или исследований.",
  },
  {
    heading: "Как используются коммерческие ссылки",
    body: "Блог не продаёт размещение. Ссылки на услуги агентства — внутренние и обозначены как наши. Если материал когда-либо будет содержать оплаченное упоминание, это будет указано в тексте, а ссылка получит атрибут sponsored.",
  },
];

export const Route = createFileRoute("/blog/editorial-policy")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "ru_RU" },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Блог HAY.agency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
      { name: "twitter:image:alt", content: "Блог HAY.agency" },
    ],
    links: [{ rel: "canonical", href: url }],
  }),
  component: EditorialPolicyPage,
});

function EditorialPolicyPage() {
  return (
    <AutoScale>
      <div className="min-h-screen bg-background font-sans text-foreground antialiased">
        <SpecialOffer />
        <Header />
        <main className="px-5 pt-4 lg:px-[21px] lg:pt-[28px]">
          <nav
            aria-label="Хлебные крошки"
            className="flex flex-wrap items-center gap-1.5 text-[12px] leading-[16px] text-muted-foreground lg:gap-2 lg:text-[14px]"
          >
            <Link to="/" className="text-foreground underline underline-offset-[3px] hover:text-brand-text">
              Главная
            </Link>
            <span aria-hidden="true">/</span>
            <Link to="/blog" className="text-foreground underline underline-offset-[3px] hover:text-brand-text">
              Блог
            </Link>
            <span aria-hidden="true">/</span>
            <span>Редакционная политика</span>
          </nav>

          <h1 className="section-title mt-8 lg:mt-[46px]">Редакционная политика блога HAY.agency</h1>

          <div className="mt-8 max-w-[760px] lg:mt-10">
            {SECTIONS.map((section) => (
              <section key={section.heading}>
                <h2 className="mt-10 text-[22px] font-medium leading-[28px] lg:text-[26px]">{section.heading}</h2>
                <p className="mt-4 text-[16px] leading-[27px] text-foreground/85 lg:text-[17px] lg:leading-[29px]">
                  {section.body}
                </p>
              </section>
            ))}
            <p className="mt-10 text-[16px] leading-[27px] text-foreground/85 lg:text-[17px]">
              Кто именно готовит материалы — на странице{" "}
              <Link
                to="/blog/authors/hay-agency"
                className="underline underline-offset-[3px] transition-colors duration-300 hover:text-brand-text"
              >
                редакции HAY.agency
              </Link>
              .
            </p>
          </div>
        </main>
        <div className="h-14 lg:h-[110px]" />
        <Footer />
        <ScrollToTop />
      </div>
    </AutoScale>
  );
}
